// 카메라 컨트롤러 클래스
class CameraController {
  constructor(camera) {
    this.camera = camera;
    this.target = null;
    this.offset = new THREE.Vector3(0, 15, 20); // 카메라 오프셋 (x, y, z)
    this.lerpSpeed = 0.05; // 카메라 추적 속도

    // 회전 애니메이션 관련
    this.isRotating = false; // 회전 중 여부
    this.rotationProgress = 0; // 회전 진행도 (0~1)
    this.rotationDuration = 0.2; // 회전 지속 시간 (초)
    this.rotationStartAngle = 0; // 회전 시작 각도

    // 줌인 애니메이션 관련
    this.isZooming = false; // 줌인 중 여부
    this.zoomProgress = 0; // 줌인 진행도 (0~1)
    this.zoomDuration = 1.0; // 줌인 지속 시간 (초)
    this.zoomTarget = null; // 줌인 타겟
    this.zoomScale = 1.0; // 줌인 배율
    this.originalOffset = new THREE.Vector3(); // 원래 오프셋 저장
  }

  // 추적할 타겟 설정 (플레이어)
  setTarget(target) {
    this.target = target;
  }

  // 카메라 오프셋 설정
  setOffset(x, y, z) {
    this.offset.set(x, y, z);
  }

  // 카메라 추적 속도 설정
  setLerpSpeed(speed) {
    this.lerpSpeed = speed;
  }

  // 카메라 업데이트 (타겟 추적)
  update(delta = 0.016) {
    if (!this.target || !this.target.isModelLoaded()) {
      return;
    }

    const targetPosition = this.target.getPosition();

    // 줌인 애니메이션 중이면 줌인 로직 실행
    if (this.isZooming) {
      this.updateZoom(delta);
      return;
    }

    // 회전 애니메이션 중이면 회전 로직 실행
    if (this.isRotating) {
      this.updateRotation(delta, targetPosition);
      return;
    }

    // 카메라가 플레이어 뒤쪽 위에서 따라가도록 설정
    const desiredCameraPosition = targetPosition.clone().add(this.offset);

    // 부드러운 카메라 이동
    this.camera.position.lerp(desiredCameraPosition, this.lerpSpeed);

    // 카메라가 항상 플레이어를 바라보도록 설정
    this.camera.lookAt(
      targetPosition.x,
      targetPosition.y + 2, // 플레이어 중심 높이에 맞게 조정
      targetPosition.z
    );
  }

  // 카메라 위치 리셋
  reset() {
    if (this.target && this.target.isModelLoaded()) {
      const targetPosition = this.target.getPosition();
      const resetPosition = targetPosition.clone().add(this.offset);
      this.camera.position.copy(resetPosition);
      this.camera.lookAt(targetPosition);
    }
  }

  // 카메라 모드 변경 (예: 1인칭, 3인칭 등)
  setMode(mode) {
    switch (mode) {
      case "close":
        this.setOffset(0, 5, 8);
        break;
      case "medium":
        this.setOffset(0, 10, 12);
        break;
      case "far":
        this.setOffset(0, 15, 15);
        break;
      case "top":
        this.setOffset(0, 25, 5);
        break;
      default:
        this.setOffset(0, 15, 15);
    }
  }

  // 카메라 회전 시작 (레벨업 시 호출)
  startRotation(duration = 0.2) {
    if (!this.target || !this.target.isModelLoaded()) {
      return;
    }

    this.isRotating = true;
    this.rotationProgress = 0;
    this.rotationDuration = duration; // 호출부에서 설정한 회전 지속 시간

    // 현재 카메라의 각도 계산
    const targetPosition = this.target.getPosition();
    const dx = this.camera.position.x - targetPosition.x;
    const dz = this.camera.position.z - targetPosition.z;
    this.rotationStartAngle = Math.atan2(dz, dx);
  }

  // 회전 애니메이션 업데이트
  updateRotation(delta, targetPosition) {
    // 타겟이 없으면 애니메이션 종료 및 초기 위치로 리셋
    if (!targetPosition || !this.target || !this.target.isModelLoaded()) {
      this.isRotating = false;
      this.rotationProgress = 0;
      this.reset(); // 카메라를 초기 위치로 리셋
      return;
    }

    // 진행도 업데이트
    this.rotationProgress += delta / this.rotationDuration;

    if (this.rotationProgress >= 1.0) {
      // 회전 완료 - 카메라를 초기 위치로 리셋
      this.isRotating = false;
      this.rotationProgress = 0;
      this.reset(); // 카메라를 초기 위치로 리셋
      return;
    }

    // 현재 각도 계산 (360도 = 2π 라디안 회전)
    const currentAngle =
      this.rotationStartAngle + Math.PI * 2 * this.rotationProgress;

    // 카메라와 타겟 사이의 거리 (오프셋의 수평 거리)
    const radius = Math.sqrt(
      this.offset.x * this.offset.x + this.offset.z * this.offset.z
    );

    // 새로운 카메라 위치 계산
    const newX = targetPosition.x + Math.cos(currentAngle) * radius;
    const newZ = targetPosition.z + Math.sin(currentAngle) * radius;
    const newY = targetPosition.y + this.offset.y;

    // 카메라 위치 설정
    this.camera.position.set(newX, newY, newZ);

    // 카메라가 플레이어를 바라보도록 설정
    this.camera.lookAt(
      targetPosition.x,
      targetPosition.y + 2,
      targetPosition.z
    );
  }

  // 특정 타겟으로 줌인 시작 (업그레이드 시 호출)
  startZoomOnTarget(target, zoomScale = 1.5, duration = 1.0) {
    if (!target || !target.isModelLoaded()) {
      return;
    }

    // 원래 오프셋과 카메라 VIEW_SIZE 저장
    this.originalOffset.copy(this.offset);

    // 원래 카메라 위치 저장 (부드러운 전환을 위해)
    this.originalCameraPosition = this.camera.position.clone();

    // 직교 카메라의 경우 원래 VIEW_SIZE 저장
    if (this.camera.isOrthographicCamera) {
      const aspect = window.innerWidth / window.innerHeight;
      this.originalViewSize = GameConfig.CAMERA.VIEW_SIZE;
      this.originalLeft = -this.originalViewSize * aspect;
      this.originalRight = this.originalViewSize * aspect;
      this.originalTop = this.originalViewSize;
      this.originalBottom = -this.originalViewSize;
    }

    // 줌인 설정
    this.isZooming = true;
    this.zoomProgress = 0;
    this.zoomDuration = duration;
    this.zoomTarget = target;
    this.zoomScale = zoomScale;
  }

  // 줌인 애니메이션 업데이트
  updateZoom(delta) {
    if (!this.zoomTarget || !this.zoomTarget.isModelLoaded()) {
      // 타겟이 없으면 애니메이션 종료 및 초기 위치로 리셋
      this.isZooming = false;
      this.zoomProgress = 0;
      this.zoomTarget = null;
      if (this.originalOffset) {
        this.offset.copy(this.originalOffset);
      }
      this.reset(); // 카메라를 초기 위치로 리셋
      return;
    }

    // 진행도 업데이트 (절대 시간으로 계산)
    this.zoomProgress += delta;

    const targetPosition = this.zoomTarget.getPosition();

    // 총 애니메이션 시간: 0.5초(줌인) + duration(유지) + 0.5초(줌아웃)
    const totalDuration = 1.0 + this.zoomDuration;

    if (this.zoomProgress >= totalDuration) {
      // 줌아웃 완료 - 원래 상태로 복귀
      this.isZooming = false;
      this.zoomProgress = 0;
      this.zoomTarget = null;
      this.offset.copy(this.originalOffset);

      // 직교 카메라의 경우 원래 VIEW_SIZE로 복원
      if (this.camera.isOrthographicCamera) {
        this.camera.left = this.originalLeft;
        this.camera.right = this.originalRight;
        this.camera.top = this.originalTop;
        this.camera.bottom = this.originalBottom;
        this.camera.updateProjectionMatrix();
      }

      // 카메라를 초기 위치로 리셋하여 항상 올바른 위치로 돌아가도록 보장
      this.reset();
      return;
    }

    // 줌인/유지/줌아웃 진행도 계산
    // 0~0.5초: 줌인, 0.5~(0.5+duration)초: 유지, (0.5+duration)~(1+duration)초: 줌아웃
    let easedProgress;
    const zoomInDuration = 0.5;
    const zoomOutStart = zoomInDuration + this.zoomDuration;

    if (this.zoomProgress < zoomInDuration) {
      // 줌인 단계 (0 -> 1)
      const t = this.zoomProgress / zoomInDuration;
      easedProgress = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    } else if (this.zoomProgress < zoomOutStart) {
      // 유지 단계 (1.0 유지)
      easedProgress = 1.0;
    } else {
      // 줌아웃 단계 (1 -> 0)
      const t = (this.zoomProgress - zoomOutStart) / zoomInDuration;
      const zoomOutProgress =
        t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      easedProgress = 1.0 - zoomOutProgress; // 줌아웃은 반대 방향
    }

    // 직교 카메라의 경우 VIEW_SIZE를 조정하여 줌인 효과
    if (this.camera.isOrthographicCamera) {
      const aspect = window.innerWidth / window.innerHeight;

      // 줌인된 VIEW_SIZE 계산 (VIEW_SIZE를 줌 배율로 나눔)
      const zoomedViewSize = this.originalViewSize / this.zoomScale;

      // 현재 VIEW_SIZE를 원래와 줌인 사이에서 lerp
      const currentViewSize =
        this.originalViewSize +
        (zoomedViewSize - this.originalViewSize) * easedProgress;

      // 카메라 projection 업데이트
      this.camera.left = -currentViewSize * aspect;
      this.camera.right = currentViewSize * aspect;
      this.camera.top = currentViewSize;
      this.camera.bottom = -currentViewSize;
      this.camera.updateProjectionMatrix();
    }

    // 줌인 오프셋 계산 (원래 오프셋을 줌 배율로 나눔)
    const zoomedOffset = this.originalOffset
      .clone()
      .divideScalar(this.zoomScale);

    // 현재 오프셋을 원래와 줌인 사이에서 lerp
    this.offset.lerpVectors(this.originalOffset, zoomedOffset, easedProgress);

    // 한국어 주석: 줌아웃 단계에서는 원래 타겟(플레이어)로 부드럽게 전환
    let finalTargetPosition = targetPosition.clone();
    let lookAtTarget = new THREE.Vector3(
      targetPosition.x,
      targetPosition.y + 2,
      targetPosition.z
    );

    // 줌아웃 단계에서 원래 타겟으로 블렌딩
    if (
      this.zoomProgress >= zoomOutStart &&
      this.target &&
      this.target.isModelLoaded()
    ) {
      const mainTargetPosition = this.target.getPosition();
      const blendFactor = (this.zoomProgress - zoomOutStart) / zoomInDuration;

      // 타겟 위치를 zoomTarget에서 원래 target으로 lerp
      finalTargetPosition.lerp(mainTargetPosition, blendFactor);

      // lookAt 타겟도 블렌딩
      lookAtTarget.lerp(
        new THREE.Vector3(
          mainTargetPosition.x,
          mainTargetPosition.y + 2,
          mainTargetPosition.z
        ),
        blendFactor
      );
    }

    // 원래 카메라 위치와 타겟 위치 사이를 부드럽게 전환
    const desiredCameraPosition = finalTargetPosition.clone().add(this.offset);

    // 줌인 시작 시의 원래 카메라 위치에서 타겟 위치로 부드럽게 이동
    // 줌인/줌아웃 단계에 따라 다른 lerp 속도 적용
    let positionLerpSpeed;
    if (this.zoomProgress < zoomInDuration) {
      // 줌인 단계: 빠르게 이동
      positionLerpSpeed = 0.15;
    } else if (this.zoomProgress < zoomOutStart) {
      // 유지 단계: 타겟을 부드럽게 따라감
      positionLerpSpeed = 0.1;
    } else {
      // 줌아웃 단계: 원래 위치로 부드럽게 복귀
      positionLerpSpeed = 0.15;
    }

    this.camera.position.lerp(desiredCameraPosition, positionLerpSpeed);

    // 한국어 주석: 카메라가 타겟을 바라보도록 부드럽게 회전
    // 목표 방향을 향하는 quaternion 계산
    const m = new THREE.Matrix4();
    m.lookAt(this.camera.position, lookAtTarget, this.camera.up);
    const targetQuaternion = new THREE.Quaternion();
    targetQuaternion.setFromRotationMatrix(m);

    // 현재 quaternion에서 목표 quaternion으로 slerp (부드러운 회전)
    this.camera.quaternion.slerp(targetQuaternion, 0.1);
  }
}

// webpack 빌드를 위해 default export 추가
export default CameraController;
